import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { CraDeclared, CraDetection, CraOverview, CraProduct, CraReport, CraRequirement } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { CriticitaBadge } from '../components/BusinessApps'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard } from '../components/common'
import { BadgePasso, etichettaCategoria, etichettaPasso, etichettaRuolo, FontiSfruttata, giornoCra, quandoCra } from '../components/Cra'
import { FinestraImportaBusinessApp, FinestraNuovaSegnalazione, FinestraProdotto } from '../components/CraFinestre'
import { DataTable, type Column } from '../components/DataTable'
import { nonLicenziato } from '../components/Sbom'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Sicurezza → CRA Compliance: the Cyber Resilience Act register of the
 * software the organisation places on the market. Products (in scope or not,
 * the role), what to assess (actively exploited vulnerabilities in supported
 * releases), the art. 14 reports with their clocks, the requirements of
 * Annex I part II. The view is in the URL. See cra_service.
 */

type Vista = 'panoramica' | 'prodotti' | 'valutare' | 'segnalazioni' | 'requisiti'

const VISTE: Array<{ key: Vista; label: () => string }> = [
  { key: 'panoramica', label: () => t('Panoramica') },
  { key: 'prodotti', label: () => t('Prodotti') },
  { key: 'valutare', label: () => t('Da valutare') },
  { key: 'segnalazioni', label: () => t('Segnalazioni') },
  { key: 'requisiti', label: () => t('Requisiti') },
]

const n = (x: number) => x.toLocaleString(localeCorrente())


export default function Cra() {
  const [params, setParams] = useSearchParams()
  const vista = (VISTE.find((v) => v.key === params.get('vista'))?.key ?? 'panoramica') as Vista
  const dati = useAsync(() => api.cra(), [])
  const scrive = usePermessiModuli().scrive('cra')
  const [lavoro, setLavoro] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const vai = (v: Vista) => setParams(v === 'panoramica' ? {} : { vista: v })

  async function ricontrolla() {
    setErrore(null)
    setLavoro(true)
    try {
      const r = await api.craRefresh()
      setEsito(r.new_detections ? t('{n} nuove vulnerabilità sfruttate da valutare.', { n: r.new_detections }) : t('Nessuna novità.'))
      void dati.reload()
    } catch (e) { setErrore(e) } finally { setLavoro(false) }
  }

  const header = (
    <header className="page__header">
      <div>
        <h1 className="page__title">{t('CRA Compliance')}</h1>
        <p className="page__subtitle">{t('Il registro del Cyber Resilience Act (Reg. UE 2024/2847) per il software che l’organizzazione mette sul mercato: prodotti e rilasci con il loro SBOM, le vulnerabilità sfruttate attivamente da valutare, le segnalazioni dell’art. 14 con i loro termini, i requisiti dell’allegato I.')}</p>
      </div>
      {scrive && <button type="button" onClick={() => void ricontrolla()} disabled={lavoro}>{lavoro ? t('Controllo in corso…') : t('Ricontrolla adesso')}</button>}
    </header>
  )
  if (dati.error && nonLicenziato(dati.error)) {
    return <>{header}<Card><EmptyState title={t('Non compreso nella licenza')} /></Card></>
  }
  const o = dati.data
  return (
    <>
      {header}
      <nav className="tabs" role="tablist">
        {VISTE.map((v) => (
          <button key={v.key} role="tab" aria-selected={vista === v.key} className={`tabs__tab${vista === v.key ? ' tabs__tab--active' : ''}`}
            onClick={() => vai(v.key)}>{v.label()}</button>
        ))}
      </nav>
      <ErrorBanner error={dati.error ?? errore} />
      {esito && <div className="banner banner--info">{esito}</div>}
      {o && !o.reporting_applies && (
        <div className="banner banner--info">{t('Gli obblighi di segnalazione dell’art. 14 si applicano dall’11 settembre 2026 (art. 71(2)), anche ai prodotti già sul mercato (art. 69(3)).')}</div>
      )}
      {dati.loading && !o ? <Spinner /> : o && (
        <>
          {vista === 'panoramica' && <Panoramica o={o} vai={vai} />}
          {vista === 'prodotti' && <Prodotti scrive={scrive} />}
          {vista === 'valutare' && <DaValutare scrive={scrive} />}
          {vista === 'segnalazioni' && <Segnalazioni scrive={scrive} />}
          {vista === 'requisiti' && <Requisiti scrive={scrive} />}
        </>
      )}
    </>
  )
}

/** For sorting: critical first. */
function rangoCriticita(c: string | null | undefined): number {
  return ({ critical: 4, high: 3, medium: 2, low: 1 } as Record<string, number>)[c ?? ''] ?? 0
}

function Panoramica({ o, vai }: { o: CraOverview; vai: (v: Vista) => void }) {
  const p = o.reports.next
  return (
    <>
      <div className="stats">
        <StatCard label={t('Prodotti nel CRA')} value={n(o.products.in_scope)} hint={t('{n} fuori ambito', { n: o.products.out_of_scope })} onClick={() => vai('prodotti')} />
        <StatCard label={t('Rilasci supportati')} value={n(o.releases.supported)}
          hint={o.releases.supported_without_sbom ? t('{n} senza SBOM', { n: o.releases.supported_without_sbom }) : t('tutti con SBOM')}
          tone={o.releases.supported_without_sbom ? 'warn' : undefined} onClick={() => vai('prodotti')} />
        <StatCard label={t('Sfruttate da valutare')} value={n(o.detections_to_assess)} tone={o.detections_to_assess ? 'error' : 'ok'}
          hint={t('CISA KEV o ENISA EUVD, nei rilasci supportati')} onClick={() => vai('valutare')} />
        <StatCard label={t('Segnalazioni aperte')} value={n(o.reports.open)} onClick={() => vai('segnalazioni')} />
        <StatCard label={t('Termini scaduti')} value={n(o.reports.steps_overdue)} tone={o.reports.steps_overdue ? 'error' : 'ok'} onClick={() => vai('segnalazioni')} />
        <StatCard label={t('Senza presa di conoscenza')} value={n(o.reports.awaiting_awareness)} tone={o.reports.awaiting_awareness ? 'warn' : undefined}
          hint={t('i termini non sono ancora partiti')} onClick={() => vai('segnalazioni')} />
        <StatCard label={t('Prodotti senza contatto')} value={n(o.products.without_contact)} tone={o.products.without_contact ? 'warn' : 'ok'}
          hint={t('punto di contatto per le vulnerabilità')} onClick={() => vai('requisiti')} />
        <StatCard label={t('Prodotti senza periodo di supporto')} value={n(o.products.without_support)} tone={o.products.without_support ? 'warn' : 'ok'} onClick={() => vai('requisiti')} />
        <StatCard label={t('Prodotti critici o importanti')} value={n(o.products.critical_in_scope)}
          hint={t('per l’organizzazione: dalla Business App o scelta a mano')} onClick={() => vai('prodotti')} />
        <StatCard label={t('Critici senza rilasci')} value={n(o.products.critical_without_releases)}
          tone={o.products.critical_without_releases ? 'warn' : 'ok'}
          hint={t('senza un rilascio con SBOM il registro non può valutarli')} onClick={() => vai('prodotti')} />
        {o.products.to_qualify > 0 && (
          <StatCard label={t('Da qualificare')} value={n(o.products.to_qualify)} tone="warn"
            hint={t('importati dalle Business App: nel campo del CRA o no?')} onClick={() => vai('prodotti')} />
        )}
      </div>
      {p && (
        <Card title={t('Prossimo termine')}>
          <p style={{ margin: 0 }}>
            <Link to={`/cra/reports/${p.report_id}`} className="mono">{p.reference}</Link> · {etichettaPasso(p.step)} · {t('entro {q}', { q: quandoCra(p.due_at) })}{' '}
            <BadgePasso p={p} />
          </p>
        </Card>
      )}
      {o.products.total === 0 && (
        <Card>
          <EmptyState title={t('Nessun prodotto ancora')} hint={t('Comincia dai prodotti: quelli nel campo del CRA, il ruolo dell’organizzazione, i progetti che li costruiscono. I rilasci arrivano dai tag con SBOM o si aggiungono a mano.')} />
          <div className="toolbar"><button type="button" onClick={() => vai('prodotti')}>{t('Vai ai prodotti')}</button></div>
        </Card>
      )}
    </>
  )
}

function Prodotti({ scrive }: { scrive: boolean }) {
  const dati = useAsync(() => api.craProducts(), [])
  const [nuovo, setNuovo] = useState(false)
  const [importa, setImporta] = useState(false)
  const naviga = useNavigate()
  const colonne: Column<CraProduct>[] = [
    { key: 'name', header: t('Prodotto'), value: (p) => p.name, render: (p) => <Link to={`/cra/products/${p.id}`}>{p.name}</Link> },
    { key: 'in_scope', header: t('CRA'), value: (p) => (p.in_scope ? t('nel campo') : t('fuori')), render: (p) => (
      <span className={`badge ${p.in_scope ? 'badge--info' : 'badge--muted'}`}>{p.in_scope ? t('nel campo') : t('fuori')}</span>) },
    { key: 'criticality', header: t('Criticità'), value: (p) => rangoCriticita(p.criticality),
      render: (p) => <span title={p.business_app_name ?? undefined}>{p.criticality ? <CriticitaBadge value={p.criticality} /> : <small className="text-muted">{t('da definire')}</small>}</span> },
    { key: 'role', header: t('Ruolo'), value: (p) => etichettaRuolo(p.role) },
    { key: 'category', header: t('Categoria di legge'), value: (p) => etichettaCategoria(p.category) },
    { key: 'supported_releases', header: t('Rilasci supportati'), type: 'number', value: (p) => p.supported_releases },
    { key: 'open_detections', header: t('Sfruttate aperte'), type: 'number', value: (p) => p.open_detections,
      render: (p) => p.open_detections ? <span className="badge badge--error">{p.open_detections}</span> : '0' },
    { key: 'open_reports', header: t('Segnalazioni aperte'), type: 'number', value: (p) => p.open_reports },
    { key: 'support_until', header: t('Supporto fino al'), type: 'date', value: (p) => p.support_until, render: (p) => giornoCra(p.support_until) },
    { key: 'vuln_contact', header: t('Contatto'), value: (p) => p.vuln_contact, render: (p) => p.vuln_contact || <span className="badge badge--warn">{t('manca')}</span> },
  ]
  return (
    <Card>
      <ErrorBanner error={dati.error} />
      <div className="toolbar">
        <button type="button" disabled={!scrive} onClick={() => setNuovo(true)}>{t('Nuovo prodotto')}</button>
        <button type="button" className="secondary" disabled={!scrive} onClick={() => setImporta(true)}>{t('Importa da Business App')}</button>
      </div>
      {dati.loading && !dati.data ? <Spinner /> : (
        <DataTable rows={dati.data ?? []} rowKey={(p) => p.id} columns={colonne} conteggio={{ nome: t('prodotti') }}
          emptyText={t('Nessun prodotto: aggiungi quelli che l’organizzazione mette sul mercato.')} />
      )}
      {nuovo && <FinestraProdotto onChiudi={() => setNuovo(false)} onFatto={(p) => naviga(`/cra/products/${p.id}`)} />}
      {importa && <FinestraImportaBusinessApp collegate={new Set((dati.data ?? []).map((p) => p.business_app_id ?? '').filter(Boolean))}
        onChiudi={() => setImporta(false)} onFatto={() => { setImporta(false); void dati.reload() }} />}
    </Card>
  )
}

function DaValutare({ scrive }: { scrive: boolean }) {
  const [tutte, setTutte] = useState(false)
  const dati = useAsync(() => api.craDetections(!tutte), [tutte])
  const [errore, setErrore] = useState<unknown>(null)
  const naviga = useNavigate()

  async function apri(d: CraDetection) {
    setErrore(null)
    try {
      const r = await api.craReportOpen({ product_id: d.product_id, kind: 'vulnerability', detection_id: d.id })
      naviga(`/cra/reports/${r.id}`)
    } catch (e) { setErrore(e) }
  }

  const colonne: Column<CraDetection>[] = [
    { key: 'vuln_id', header: t('Vulnerabilità'), nowrap: true, value: (d) => [d.vuln_id, ...d.aliases].join(' '), render: (d) => <span className="mono">{d.vuln_id}</span> },
    { key: 'product', header: t('Prodotto'), value: (d) => d.product, render: (d) => <Link to={`/cra/products/${d.product_id}`}>{d.product}</Link> },
    { key: 'criticita', header: t('Criticità'), value: (d) => rangoCriticita(d.product_criticality),
      render: (d) => (d.product_criticality ? <CriticitaBadge value={d.product_criticality} /> : <small className="text-muted">—</small>) },
    { key: 'component', header: t('Componente'), value: (d) => `${d.component} ${d.versions.join(' ')}`,
      render: (d) => <><span className="mono">{d.component}</span> <small className="text-muted">{d.versions.join(', ')}</small></> },
    { key: 'sources', header: t('Sfruttata secondo'), value: (d) => d.sources.join(' '), render: (d) => <FontiSfruttata fonti={d.sources} euvd={d.euvd_id} /> },
    { key: 'fixed', header: t('Corretta in'), value: (d) => d.fixed, mono: true },
    { key: 'first_seen', header: t('Vista da CENSO'), type: 'date', value: (d) => d.first_seen, render: (d) => quandoCra(d.first_seen) },
    { key: 'stato', header: t('Stato'), value: (d) => (d.report_id ? 'segnalata' : d.resolved_at ? d.resolved_reason : 'aperta'), render: (d) => (
      d.report_id ? <Link to={`/cra/reports/${d.report_id}`}>{t('segnalazione aperta')}</Link>
        : d.resolved_at ? <span className="badge badge--muted">{d.resolved_reason === 'vex' ? t('esclusa da una VEX') : t('non più nei rilasci supportati')}</span>
          : <button type="button" disabled={!scrive} onClick={() => void apri(d)}>{t('Apri la segnalazione')}</button>) },
  ]
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>{t('Le vulnerabilità sfruttate attivamente (catalogo CISA KEV o elenco ENISA EUVD) nei rilasci supportati dei prodotti nel campo del CRA, non escluse da una valutazione VEX. Ognuna va valutata: se il prodotto è davvero interessato, si apre la segnalazione; se non lo è, si scrive la VEX dal prodotto.')}</p>
      <ErrorBanner error={dati.error ?? errore} />
      <div className="checkbox"><input id="anche-risolte" type="checkbox" checked={tutte} onChange={(e) => setTutte(e.target.checked)} /><label htmlFor="anche-risolte">{t('Anche quelle risolte')}</label></div>
      {dati.loading && !dati.data ? <Spinner /> : (
        <DataTable rows={dati.data ?? []} rowKey={(d) => d.id} columns={colonne} conteggio={{ nome: t('vulnerabilità') }}
          initialSort={{ key: 'criticita', dir: 'desc' }} emptyText={t('Niente da valutare.')} />
      )}
    </Card>
  )
}

export function TabellaSegnalazioni({ righe, conProdotto = true }: { righe: CraReport[]; conProdotto?: boolean }) {
  const colonne: Column<CraReport>[] = [
    { key: 'reference', header: t('Riferimento'), nowrap: true, value: (r) => r.reference, render: (r) => <Link to={`/cra/reports/${r.id}`} className="mono">{r.reference}</Link> },
    { key: 'title', header: t('Titolo'), value: (r) => r.title },
    ...(conProdotto ? [{ key: 'product', header: t('Prodotto'), value: (r: CraReport) => r.product },
      { key: 'criticita', header: t('Criticità'), value: (r: CraReport) => rangoCriticita(r.product_criticality),
        render: (r: CraReport) => (r.product_criticality ? <CriticitaBadge value={r.product_criticality} /> : <small className="text-muted">—</small>) }] : []),
    { key: 'kind', header: t('Tipo'), value: (r) => (r.kind === 'incident' ? t('incidente grave') : t('vulnerabilità sfruttata')) },
    { key: 'aware_at', header: t('Presa di conoscenza'), type: 'date', value: (r) => r.aware_at, render: (r) => r.aware_at ? quandoCra(r.aware_at) : <span className="badge badge--warn">{t('da confermare')}</span> },
    { key: 'next', header: t('Prossimo passo'), value: (r) => r.next_step?.due_at ?? '', render: (r) => r.status === 'closed' ? <span className="badge badge--muted">{t('chiusa')}</span>
      : r.next_step ? <>{etichettaPasso(r.next_step.step, r.kind)} <BadgePasso p={r.next_step} /></> : <small className="text-muted">—</small> },
  ]
  return <DataTable rows={righe} rowKey={(r) => r.id} columns={colonne} conteggio={{ nome: t('segnalazioni') }}
    initialSort={{ key: 'next', dir: 'asc' }} emptyText={t('Nessuna segnalazione.')} />
}

function Segnalazioni({ scrive }: { scrive: boolean }) {
  const dati = useAsync(() => api.craReports(), [])
  const prodotti = useAsync(() => api.craProducts(), [])
  const [nuova, setNuova] = useState(false)
  const naviga = useNavigate()
  const nelCampo = (prodotti.data ?? []).filter((p) => p.in_scope)
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>{t('Le segnalazioni dell’art. 14 alla piattaforma unica di ENISA, verso il CSIRT coordinatore: i termini partono dalla presa di conoscenza confermata da una persona.')}</p>
      <ErrorBanner error={dati.error} />
      <div className="toolbar"><button type="button" disabled={!scrive || nelCampo.length === 0} onClick={() => setNuova(true)}>{t('Nuova segnalazione')}</button></div>
      {dati.loading && !dati.data ? <Spinner /> : <TabellaSegnalazioni righe={dati.data ?? []} />}
      {nuova && <FinestraNuovaSegnalazione prodotti={nelCampo} onChiudi={() => setNuova(false)} onFatto={(id) => naviga(`/cra/reports/${id}`)} />}
    </Card>
  )
}

const STATI_REQ: Record<string, [string, () => string]> = {
  coperto: ['badge--ok', () => t('coperto')], parziale: ['badge--warn', () => t('parziale')],
  assente: ['badge--error', () => t('assente')], non_applicabile: ['badge--muted', () => t('non applicabile')],
}

export function BadgeRequisito({ s }: { s: string }) {
  const [tono, l] = STATI_REQ[s] ?? ['badge--muted', () => s]
  return <span className={`badge ${tono}`}>{l()}</span>
}

export function TabellaRequisiti({ righe }: { righe: CraRequirement[] }) {
  return (
    <DataTable rows={righe} rowKey={(r) => r.id} paginazione={false} strumenti={false} columns={[
      { key: 'articolo', header: t('Riferimento'), value: (r) => r.articolo, nowrap: true },
      { key: 'titolo', header: t('Requisito'), value: (r) => t(r.titolo) },
      { key: 'stato', header: t('Stato'), value: (r) => r.stato, render: (r) => <BadgeRequisito s={r.stato} /> },
    ]} />
  )
}

function Dichiarazione({ v, scrive, onFatto }: { v: CraDeclared; scrive: boolean; onFatto: () => void }) {
  const d = v.declaration
  const [stato, setStato] = useState(d?.stato ?? 'assente')
  const [nota, setNota] = useState(d?.nota ?? '')
  const [rif, setRif] = useState(d?.riferimento ?? '')
  const [resp, setResp] = useState(d?.responsabile ?? '')
  const [errore, setErrore] = useState<unknown>(null)
  async function salva() {
    setErrore(null)
    try { await api.craDeclare(v.id, { stato, nota, riferimento: rif, responsabile: resp }); onFatto() } catch (e) { setErrore(e) }
  }
  return (
    <div style={{ borderTop: '1px solid var(--divider)', padding: '10px 0' }}>
      <p style={{ margin: '0 0 4px' }}><strong>{t(v.titolo)}</strong> <small className="text-muted">{v.articolo}</small> {d && <BadgeRequisito s={d.stato} />}</p>
      <p className="field__hint" style={{ margin: '0 0 6px' }}>{t(v.hint)}</p>
      <ErrorBanner error={errore} />
      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 160 }}>
          <label htmlFor={`d-${v.id}`}>{t('Stato')}</label>
          <select id={`d-${v.id}`} value={stato} disabled={!scrive} onChange={(e) => setStato(e.target.value)}>
            {Object.entries(STATI_REQ).map(([k, [, l]]) => <option key={k} value={k}>{l()}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor={`n-${v.id}`}>{t('Nota')}</label>
          <input id={`n-${v.id}`} type="text" value={nota} disabled={!scrive} onChange={(e) => setNota(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label htmlFor={`r-${v.id}`}>{t('Documento di riferimento')}</label>
          <input id={`r-${v.id}`} type="text" value={rif} disabled={!scrive} onChange={(e) => setRif(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 180 }}>
          <label htmlFor={`p-${v.id}`}>{t('Responsabile')}</label>
          <input id={`p-${v.id}`} type="text" value={resp} disabled={!scrive} onChange={(e) => setResp(e.target.value)} />
        </div>
        <button type="button" className="secondary" disabled={!scrive} onClick={() => void salva()}>{t('Salva')}</button>
      </div>
      {d?.updated_by && <small className="text-muted">{t('Dichiarato da {chi}', { chi: d.updated_by })}</small>}
    </div>
  )
}

function Requisiti({ scrive }: { scrive: boolean }) {
  const dati = useAsync(() => api.craRequirements(), [])
  return (
    <>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : dati.data && (
        <>
          {dati.data.products.length === 0 && <Card><EmptyState title={t('Nessun prodotto nel campo del CRA')} /></Card>}
          {dati.data.products.map((p) => (
            <Card key={p.id} title={p.name}>
              <TabellaRequisiti righe={p.items} />
              <p style={{ marginBottom: 0 }}><Link to={`/cra/products/${p.id}`}>{t('Apri il prodotto')}</Link></p>
            </Card>
          ))}
          <Card title={t('Dichiarati dall’organizzazione')}>
            <p className="field__hint" style={{ marginTop: 0 }}>{t('Ciò che i dati non misurano (allegato I, parte II): lo dichiara l’organizzazione, con una nota e il documento che lo prova.')}</p>
            {dati.data.declared.map((v) => <Dichiarazione key={v.id} v={v} scrive={scrive} onFatto={() => void dati.reload()} />)}
          </Card>
        </>
      )}
    </>
  )
}
